"use client";

import { create } from "zustand";
import { NotificationShowFilter, NotificationSortOption } from "@/types/notification";

interface NotificationStore {
  // Polling / badge state (used by NotificationManager, header, sidebar)
  unreadCount: number;
  isCountLoading: boolean;
  setUnreadCount: (count: number) => void;
  setIsCountLoading: (loading: boolean) => void;
  reset: () => void;

  // Notifications page UI state
  selectedNotificationId: number | null;
  activeCategories: string[];
  sortBy: NotificationSortOption;
  searchQuery: string;
  /** Committed on Enter — used for breadcrumb & heading, not live filtering */
  committedSearch: string;
  perPage: number;
  currentPage: number;
  showFilters: NotificationShowFilter[];
  setSelectedNotification: (id: number | null) => void;
  toggleShowFilter: (filter: NotificationShowFilter) => void;
  clearShowFilters: () => void;
  toggleCategory: (id: string) => void;
  clearCategories: () => void;
  setShowFiltersBulk: (filters: NotificationShowFilter[]) => void;
  setActiveCategories: (ids: string[]) => void;
  setSortBy: (sort: NotificationSortOption) => void;
  setSearchQuery: (query: string) => void;
  setCommittedSearch: (query: string) => void;
  setPerPage: (count: number) => void;
  setCurrentPage: (page: number) => void;
}

export const useNotificationStore = create<NotificationStore>((set) => ({
  unreadCount: 0,
  isCountLoading: false,
  setUnreadCount: (count) => set({ unreadCount: Number.isFinite(count) ? Math.max(0, count) : 0 }),
  setIsCountLoading: (isLoading) => set({ isCountLoading: isLoading }),
  reset: () =>
    set({
      unreadCount: 0,
      isCountLoading: false,
      selectedNotificationId: null,
      activeCategories: [],
      sortBy: "newest",
      searchQuery: "",
      committedSearch: "",
      perPage: 6,
      currentPage: 1,
      showFilters: [],
    }),

  selectedNotificationId: null,
  activeCategories: [],
  sortBy: "newest",
  searchQuery: "",
  committedSearch: "",
  perPage: 6,
  currentPage: 1,
  showFilters: [],
  setSelectedNotification: (id) => set({ selectedNotificationId: id }),
  toggleShowFilter: (filter) =>
    set((state) => ({
      showFilters: state.showFilters.includes(filter)
        ? state.showFilters.filter((f) => f !== filter)
        : [...state.showFilters, filter],
      currentPage: 1,
    })),
  clearShowFilters: () => set({ showFilters: [], currentPage: 1 }),
  toggleCategory: (slug) =>
    set((state) => ({
      activeCategories: state.activeCategories.includes(slug)
        ? state.activeCategories.filter((s) => s !== slug)
        : [...state.activeCategories, slug],
      currentPage: 1,
    })),
  clearCategories: () => set({ activeCategories: [], currentPage: 1 }),
  setShowFiltersBulk: (filters) => set({ showFilters: filters, currentPage: 1 }),
  setActiveCategories: (ids) => set({ activeCategories: ids, currentPage: 1 }),
  setSortBy: (sortBy) => set({ sortBy, currentPage: 1 }),
  setSearchQuery: (searchQuery) => set({ searchQuery, currentPage: 1 }),
  setCommittedSearch: (committedSearch) => set({ committedSearch }),
  setPerPage: (perPage) => set({ perPage, currentPage: 1 }),
  setCurrentPage: (currentPage) => set({ currentPage }),
}));
